/**
 * ClinicalDocumentationPage — entry point for the clinical documentation
 * module. Lists existing notes for the selected patient and lets
 * physicians / nurses create new notes (SOAP, H&P, Discharge, Procedure,
 * Progress) with voice dictation + AI scribe.
 *
 * Top-level structure:
 *   - Patient picker
 *   - "New Encounter" / template chooser
 *   - Note list (status badges: draft / signed / locked / amended)
 *   - Click into a note → NoteEditor inline
 *
 * Verbiage strictly per the Carlos demo spec (2026-06-07).
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  FileText, Plus, Stethoscope, ClipboardList, FileCheck, FileSignature,
  Mic, Activity, AlertCircle, Loader2,
} from "lucide-react";
import PatientDropdown from "../components/PatientDropdown";
import NoteEditor from "./clinical/NoteEditor";

const TEMPLATE_ICONS = {
  SOAP: ClipboardList, HP: Stethoscope, DISCHARGE: FileCheck,
  PROCEDURE: FileSignature, PROGRESS: FileText,
};
const STATUS_BADGES = {
  draft:   { bg: "#fef6e0", color: "#7c5210", border: "#e0b973", label: "Draft" },
  signed:  { bg: "#e9f5e9", color: "#2d6b2d", border: "#a5d5a5", label: "Signed" },
  locked:  { bg: "#e8f0fb", color: "#1f6feb", border: "#c9d6e8", label: "Locked" },
  amended: { bg: "#f4e8fb", color: "#6b1f8b", border: "#d6c9e8", label: "Amended" },
};

export default function ClinicalDocumentationPage({ user }) {
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [notes, setNotes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeNoteId, setActiveNoteId] = useState(null);  // null = list view, number = editor
  const [showNewModal, setShowNewModal] = useState(false);

  const canCreate = (user && user.permissions && user.permissions.includes("notes:create"));

  // Load patients + templates on mount
  useEffect(() => {
    (async () => {
      try {
        const [pats, tpls] = await Promise.all([
          window.zignagenixAPI.getPatients(),
          window.zignagenixAPI.invoke("notes:templates:list"),
        ]);
        setPatients(Array.isArray(pats) ? pats : []);
        setTemplates(Array.isArray(tpls) ? tpls : []);
        if (Array.isArray(pats) && pats.length > 0) {
          setSelectedPatientId(pats[0].id);
        }
      } catch (e) {
        setError((e && e.message) || "Failed to load");
      }
      setLoading(false);
    })();
  }, []);

  const loadNotes = useCallback(async () => {
    if (!selectedPatientId) { setNotes([]); return; }
    try {
      const rows = await window.zignagenixAPI.invoke("notes:list", { patientId: selectedPatientId, limit: 100 });
      setNotes(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError((e && e.message) || "Failed to load notes");
    }
  }, [selectedPatientId]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  async function createNote(templateKey) {
    setShowNewModal(false);
    try {
      const resp = await window.zignagenixAPI.invoke("notes:create", {
        patientId: selectedPatientId,
        templateKey,
      });
      if (resp && resp.error) {
        setError(resp.message || resp.code);
        return;
      }
      await loadNotes();
      setActiveNoteId(resp.id);
    } catch (e) {
      setError((e && e.message) || "Failed to create note");
    }
  }

  if (loading) return <div className="page-loading">Loading clinical documentation…</div>;

  // ── Note editor view ────────────────────────────────────────────────
  if (activeNoteId) {
    return (
      <NoteEditor
        noteId={activeNoteId}
        user={user}
        onBack={() => { setActiveNoteId(null); loadNotes(); }}
      />
    );
  }

  // ── List view ───────────────────────────────────────────────────────
  return (
    <div className="page" style={{ padding: "16px 24px" }}>

      <div className="page-header-row">
        <h1 className="page-title">Clinical Documentation</h1>
      </div>

      <div className="filter-bar" style={{ marginBottom: 18 }}>
        <label className="filter-label">Patient:</label>
        <PatientDropdown
          patients={patients}
          value={String(selectedPatientId)}
          onChange={(v) => setSelectedPatientId(Number(v))}
          placeholder="Select a patient"
          idPrefix="cdocs"
        />
        {canCreate && selectedPatientId && (
          <button className="btn-primary" onClick={() => setShowNewModal(true)}>
            <Plus size={16} /> New Encounter
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert--error" style={{ marginBottom: 14 }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* ── Template chooser modal ─────────────────────────────── */}
      {showNewModal && (
        <div style={modalBackdrop} onClick={() => setShowNewModal(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, marginTop: 0 }}>Choose note type</h2>
            <p style={{ fontSize: 12, color: "#5a6478", marginTop: -8 }}>
              Pick a template to start your encounter documentation.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
              {templates.map((t) => {
                const Icon = TEMPLATE_ICONS[t.template_key] || FileText;
                return (
                  <button
                    key={t.id}
                    onClick={() => createNote(t.template_key)}
                    style={templateButton}
                  >
                    <Icon size={20} color="#1f6feb" />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: "#5a6478", marginTop: 2 }}>
                        {t.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 14, textAlign: "right" }}>
              <button className="btn-ghost" onClick={() => setShowNewModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Notes list ─────────────────────────────────────────── */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Status</th>
              <th>Author</th>
              <th>Diagnoses</th>
              <th>AI Scribe</th>
              <th>Created</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {notes.length === 0 ? (
              <tr><td colSpan="8" className="empty-state">
                {selectedPatientId ? "No clinical notes yet. Click \"New Encounter\" to begin." : "Select a patient."}
              </td></tr>
            ) : notes.map((n) => {
              const sb = STATUS_BADGES[n.status] || STATUS_BADGES.draft;
              return (
                <tr key={n.id}>
                  <td>
                    <strong>{templateName(templates, n.template_key)}</strong>
                  </td>
                  <td>
                    <span style={{ ...statusBadge, background: sb.bg, color: sb.color, border: `1px solid ${sb.border}` }}>
                      {sb.label}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: "#5a6478" }}>{n.author_display_name || `User ${n.author_user_id}`}</td>
                  <td style={{ fontSize: 11, fontFamily: "monospace" }}>
                    {(n.icd10_codes || []).join(", ") || "—"}
                  </td>
                  <td>
                    {n.ai_scribe_used
                      ? <span style={{ fontSize: 11, color: "#1f6feb", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Mic size={11} /> {n.ai_scribe_confidence || "used"}
                        </span>
                      : <span style={{ color: "#9aa4b2", fontSize: 11 }}>—</span>}
                  </td>
                  <td style={{ fontSize: 11, color: "#5a6478" }}>{shortDate(n.created_at)}</td>
                  <td style={{ fontSize: 11, color: "#5a6478" }}>{shortDate(n.updated_at)}</td>
                  <td>
                    <button onClick={() => setActiveNoteId(n.id)} className="btn-ghost" style={{ fontSize: 11 }}>
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={complianceFooter}>
        <Activity size={12} /> All entries are time-stamped and audited per HIPAA requirements.
        <span style={{ color: "#9aa4b2" }}> · AI-generated content must be reviewed and edited by a licensed provider before finalization.</span>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────
function templateName(templates, key) {
  const t = templates.find((x) => x.template_key === key);
  return t ? t.name : key;
}
function shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleString();
}

// ─── Styles ────────────────────────────────────────────────────────────
const modalBackdrop = {
  position: "fixed", inset: 0, background: "rgba(15, 22, 36, 0.4)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};
const modalCard = {
  background: "#fff", padding: 20, borderRadius: 6, width: 520, maxWidth: "90vw",
  boxShadow: "0 6px 24px rgba(0, 0, 0, 0.15)",
};
const templateButton = {
  display: "flex", gap: 10, alignItems: "flex-start", textAlign: "left",
  padding: "12px 14px", background: "#fff", border: "1px solid #c9d6e8", borderRadius: 4,
  cursor: "pointer",
};
const statusBadge = {
  display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 11,
  fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4,
};
const complianceFooter = {
  marginTop: 18, padding: "10px 12px", fontSize: 11, color: "#5a6478",
  background: "#f4f7fb", border: "1px solid #c9d6e8", borderRadius: 4,
  display: "flex", alignItems: "center", gap: 6,
};
